import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { TICKET_PRIORITY_LABELS, TICKET_STATUS_LABELS } from '../shared/index.js';

import type { Ticket } from '../api/endpoints.js';
import { ticketApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';
import { useSession } from '../auth/session.js';
import { HeaderCell, TableHeaderRow, TableShell, tableRowClass } from '../components/data-table.js';
import { PageHeader } from '../components/page-header.js';
import { Select } from '../components/select.js';
import { Badge, Button, Spinner } from '../components/ui.js';

const PAGE_SIZE = 25;

const ROLES = ['raised', 'assigned'] as const;
type Role = (typeof ROLES)[number];

const ROLE_LABELS: Readonly<Record<Role, string>> = Object.freeze({
  raised: 'Raised by me',
  assigned: 'Assigned to me',
});

const OPEN_ONLY = 'open';
const EVERYTHING = 'all';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}

export function MyRequestsPage(): ReactNode {
  const { principal } = useSession();
  const navigate = useNavigate();

  const [role, setRole] = useState<Role>('raised');
  const [scope, setScope] = useState<string>(OPEN_ONLY);
  const [page, setPage] = useState(1);

  const me = principal?.id ?? '';

  const filters = {
    ...(role === 'raised' ? { requesterId: me } : { assigneeId: me }),
    ...(scope === OPEN_ONLY ? { includeClosed: false } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.tickets.list(filters),
    queryFn: () => ticketApi.list(filters),
    enabled: me !== '',
  });

  const open = (ticket: Ticket): void => {
    void navigate(`/boards/${ticket.boardSlug}?ticket=${ticket.id}`);
  };

  return (
    <>
      <PageHeader title="My requests" subtitle="Everything you have going, across every board." />

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="flex h-11 items-center rounded-field border border-hairline bg-white p-0.5 dark:border-dark-line dark:bg-dark-field">
            {ROLES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setRole(option);
                  setPage(1);
                }}
                className={`h-full rounded-[9px] px-3 text-[12.5px] font-bold transition ${
                  role === option
                    ? 'bg-rail-active text-white'
                    : 'text-muted hover:text-navy dark:hover:text-dark-ink'
                }`}
              >
                {ROLE_LABELS[option]}
              </button>
            ))}
          </span>

          <Select
            label="Which requests"
            value={scope}
            onChange={(next) => {
              setScope(next);
              setPage(1);
            }}
            options={[
              { value: OPEN_ONLY, label: 'Still open' },
              { value: EVERYTHING, label: 'Everything' },
            ]}
          />
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-muted">
            <Spinner className="size-5" />
          </div>
        ) : (data?.items ?? []).length === 0 ? (
          <div className="grid place-items-center px-8 py-20 text-center">
            <p className="max-w-sm text-[13.5px] leading-relaxed text-muted">
              {role === 'raised'
                ? 'You have not raised anything yet. Use New request on any board.'
                : 'Nothing is assigned to you right now.'}
            </p>
          </div>
        ) : (
          <>
            <TableShell>
              <TableHeaderRow>
                <HeaderCell className="w-16 shrink-0">Ref</HeaderCell>
                <HeaderCell className="min-w-[120px] max-w-[28rem] flex-1">Request</HeaderCell>
                <HeaderCell className="hidden w-36 shrink-0 md:block">Board</HeaderCell>
                <HeaderCell className="hidden w-40 shrink-0 lg:block">Column</HeaderCell>
                <HeaderCell className="hidden w-20 shrink-0 xl:block">Priority</HeaderCell>
                <HeaderCell className="w-24 shrink-0 text-right">Updated</HeaderCell>
              </TableHeaderRow>

              {(data?.items ?? []).map((ticket, index) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => {
                    open(ticket);
                  }}
                  className={tableRowClass(index === 0)}
                >
                  <span className="w-16 shrink-0 text-[11.5px] font-bold tracking-[0.06em] text-muted">
                    SBG#{ticket.number}
                  </span>

                  <span className="min-w-[120px] max-w-[28rem] flex-1">
                    <span className="block truncate text-[14px] font-semibold text-navy dark:text-dark-ink">
                      {ticket.title}
                    </span>
                    {ticket.status === 'closed' ? (
                      <span className="mt-0.5 block">
                        <Badge tone="neutral">{TICKET_STATUS_LABELS.closed}</Badge>
                      </span>
                    ) : null}
                  </span>

                  <span className="hidden w-36 shrink-0 truncate text-[12.5px] text-muted md:block">
                    {ticket.boardName}
                  </span>

                  <span className="hidden w-40 shrink-0 truncate text-[12.5px] text-muted lg:block">
                    {ticket.stageLabel}
                  </span>

                  <span className="hidden w-20 shrink-0 truncate text-[12.5px] text-muted xl:block">
                    {TICKET_PRIORITY_LABELS[ticket.priority]}
                  </span>

                  <span className="w-24 shrink-0 text-right text-[12px] text-muted">
                    {formatDate(ticket.updatedAt)}
                  </span>
                </button>
              ))}
            </TableShell>

            {data !== undefined && data.meta.totalPages > 1 ? (
              <div className="mt-3 flex items-center justify-end gap-2">
                <span className="text-[12.5px] text-muted">
                  Page {data.meta.page} of {data.meta.totalPages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => current - 1);
                  }}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page >= data.meta.totalPages}
                  onClick={() => {
                    setPage((current) => current + 1);
                  }}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
